import moment from 'moment'

import {
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  type SearchFilters,
  type SortKey,
  twitterBenchmarkCampaignPostsQueryRestoreSchema
} from '@/typings/twitter_benchmark_campaign_posts'

export type Order = 'asc' | 'desc'
export type PageSize = (typeof PAGE_SIZE_OPTIONS)[number]

export interface TwBenchmarkListState {
  filters: SearchFilters
  sortBy: SortKey
  order: Order
  page: number
  pageSize: PageSize
}

export const DEFAULT_SORT_BY: SortKey = 'campaignStartDatetime'
export const DEFAULT_ORDER: Order = 'desc'
export const DEFAULT_PAGE = 1

const DATE_MATCH_GRANULARITY = 'minute'

/**
 * URL クエリの 12 桁数値（yyyyMMddHHmm）を ISO8601 へ復元する（ローカルタイムとして解釈する）。
 */
const QUERY_DATETIME_FORMAT = 'YYYYMMDDHHmm'
const QUERY_DATETIME_PATTERN = /^\d{12}$/

/**
 * URL クエリの 12 桁数値（yyyyMMddHHmm）を ISO8601 へ復元する（ローカルタイムとして解釈する）。
 */
export function toQueryDatetime(value?: string): string | undefined {
  if (!value) {
    return undefined
  }

  const parsed = moment(value, moment.ISO_8601, true)
  if (!parsed.isValid()) return undefined

  return parsed.format(QUERY_DATETIME_FORMAT)
}

/**
 * URL クエリの 12 桁数値（yyyyMMddHHmm）を ISO8601 へ復元する（ローカルタイムとして解釈する）。
 */
export function fromQueryDatetime(value?: string): string | undefined {
  if (value === undefined) return undefined

  if (!QUERY_DATETIME_PATTERN.test(value)) return undefined

  const parsed = moment(value, QUERY_DATETIME_FORMAT, true)
  if (!parsed.isValid()) return undefined

  return parsed.format()
}

/**
 * デフォルト期間（30日前 0:00 〜 今日 0:00）を ISO8601 で生成する純関数（store・ピッカーと同一基準にする）。
 */
export function createDefaultDateRange(): { startDatetime: string; endDatetime: string } {
  return {
    startDatetime: moment().subtract(30, 'days').startOf('day').format(),
    endDatetime: moment().startOf('day').format()
  }
}

/**
 * 期間がデフォルト（30日前 0:00 〜 今日 0:00）と分単位で一致するか判定する（一致時は URL クエリに含めない）。
 */
export function isDefaultDateRange(startDatetime?: string, endDatetime?: string): boolean {
  if (!startDatetime || !endDatetime) return false
  const defaultStart = moment().subtract(30, 'days').startOf('day')
  const defaultEnd = moment().startOf('day')
  return (
    moment(startDatetime).isSame(defaultStart, DATE_MATCH_GRANULARITY) &&
    moment(endDatetime).isSame(defaultEnd, DATE_MATCH_GRANULARITY)
  )
}

type RouteQueryValue = string | (string | null)[] | null | undefined
export type RouteQuery = Record<string, RouteQueryValue>

/**
 * クエリ値を単一文字列へ正規化する（配列なら先頭要素）。
 */
function toSingle(value: RouteQueryValue): string | undefined {
  if (value === undefined || value === null) return undefined
  if (Array.isArray(value)) {
    const first = value.find((v): v is string => typeof v === 'string')
    return first
  }
  return value
}

/**
 * クエリ値を文字列配列へ正規化する（カンマ区切りを分割し、繰り返しキー形式も後方互換で受け付ける）。
 */
function toArray(value: RouteQueryValue): string[] | undefined {
  if (value === undefined || value === null) return undefined
  const list = Array.isArray(value) ? value : [value]
  const filtered = list
    .filter((v): v is string => typeof v === 'string')
    .flatMap(v => v.split(','))
    .map(v => v.trim())
    .filter(v => v !== '')
  return filtered.length ? filtered : undefined
}

/**
 * limit 文字列を有効なページサイズへ正規化する（不正時はデフォルト）。
 */
function toPageSize(value: RouteQueryValue): PageSize {
  const raw = toSingle(value)
  if (raw === undefined) return DEFAULT_PAGE_SIZE
  const num = Number(raw)
  return (PAGE_SIZE_OPTIONS as readonly number[]).includes(num) ? (num as PageSize) : DEFAULT_PAGE_SIZE
}

/**
 * page 文字列を 1 以上の整数へ正規化する（不正時は 1）。
 */
function toPage(value: RouteQueryValue): number {
  const raw = toSingle(value)
  if (raw === undefined) return DEFAULT_PAGE
  const num = Number(raw)
  return Number.isInteger(num) && num >= 1 ? num : DEFAULT_PAGE
}

/**
 * URL クエリ → 一覧 state（復元）。zod の復元専用スキーマで検証し、失敗キーや欠落した期間はデフォルトへ落とす。
 */
export function queryToListState(query: RouteQuery): TwBenchmarkListState {
  const normalizedForSchema: Record<string, unknown> = {
    ...query,
    industryIds: toArray(query.industryIds),
    accountIds: toArray(query.accountIds),
    campaignTypes: toArray(query.campaignTypes),
    incentives: toArray(query.incentives),
    tags: toArray(query.tags),
    // URL 上は 12 桁数値のため、スキーマ（ISO8601 前提）へ渡す前に復元する。
    startDatetime: fromQueryDatetime(toSingle(query.startDatetime)),
    endDatetime: fromQueryDatetime(toSingle(query.endDatetime)),
    isTrend: toSingle(query.isTrend),
    winnerCountUndetectable: toSingle(query.winnerCountUndetectable),
    keyword: toSingle(query.keyword),
    sortBy: toSingle(query.sortBy),
    order: toSingle(query.order)
  }

  const parsed = twitterBenchmarkCampaignPostsQueryRestoreSchema.safeParse(normalizedForSchema)
  const data = parsed.success ? parsed.data : twitterBenchmarkCampaignPostsQueryRestoreSchema.parse({})

  const defaultRange = createDefaultDateRange()
  const startDatetime = data.startDatetime ?? defaultRange.startDatetime
  const endDatetime = data.endDatetime ?? defaultRange.endDatetime

  const filters: SearchFilters = {
    startDatetime,
    endDatetime,
    industryIds: data.industryIds,
    accountIds: data.accountIds,
    campaignTypes: data.campaignTypes,
    incentives: data.incentives,
    tags: data.tags,
    keyword: data.keyword,
    isTrend: data.isTrend,
    winnerCountUndetectable: data.winnerCountUndetectable,
    winnerCountMin: data.winnerCountMin,
    winnerCountMax: data.winnerCountMax,
    followersMin: data.followersMin,
    followersMax: data.followersMax,
    impressionsCountMin: data.impressionsCountMin,
    impressionsCountMax: data.impressionsCountMax,
    likesCountMin: data.likesCountMin,
    likesCountMax: data.likesCountMax,
    retweetsCountMin: data.retweetsCountMin,
    retweetsCountMax: data.retweetsCountMax,
    quotesCountMin: data.quotesCountMin,
    quotesCountMax: data.quotesCountMax,
    repliesCountMin: data.repliesCountMin,
    repliesCountMax: data.repliesCountMax,
    reactionsCountMin: data.reactionsCountMin,
    reactionsCountMax: data.reactionsCountMax,
    reactionsRateMin: data.reactionsRateMin,
    reactionsRateMax: data.reactionsRateMax,
    campaignDaysMin: data.campaignDaysMin,
    campaignDaysMax: data.campaignDaysMax
  }

  return {
    filters,
    sortBy: data.sortBy,
    order: data.order,
    page: toPage(query.page),
    pageSize: toPageSize(query.limit)
  }
}

const NUMERIC_FILTER_KEYS: (keyof SearchFilters)[] = [
  'winnerCountMin',
  'winnerCountMax',
  'followersMin',
  'followersMax',
  'impressionsCountMin',
  'impressionsCountMax',
  'likesCountMin',
  'likesCountMax',
  'retweetsCountMin',
  'retweetsCountMax',
  'quotesCountMin',
  'quotesCountMax',
  'repliesCountMin',
  'repliesCountMax',
  'reactionsCountMin',
  'reactionsCountMax',
  'reactionsRateMin',
  'reactionsRateMax',
  'campaignDaysMin',
  'campaignDaysMax'
]

const ARRAY_FILTER_KEYS: (keyof SearchFilters)[] = ['industryIds', 'accountIds', 'campaignTypes', 'incentives', 'tags']

/**
 * 一覧 state → URL クエリ。デフォルト値と groupId は含めず、配列はカンマ区切りで文字列化する。
 */
export function listStateToQuery(state: TwBenchmarkListState): Record<string, string> {
  const query: Record<string, string> = {}

  if (state.page !== DEFAULT_PAGE) query.page = String(state.page)
  if (state.pageSize !== DEFAULT_PAGE_SIZE) query.limit = String(state.pageSize)
  if (state.sortBy !== DEFAULT_SORT_BY) query.sortBy = state.sortBy
  if (state.order !== DEFAULT_ORDER) query.order = state.order

  const { filters } = state

  if (!isDefaultDateRange(filters.startDatetime, filters.endDatetime)) {
    const startDatetime = toQueryDatetime(filters.startDatetime)
    const endDatetime = toQueryDatetime(filters.endDatetime)

    if (startDatetime !== undefined) query.startDatetime = startDatetime
    if (endDatetime !== undefined) query.endDatetime = endDatetime
  }

  for (const key of ARRAY_FILTER_KEYS) {
    const value = filters[key] as number[] | undefined
    if (value && value.length) query[key] = value.map(String).join(',')
  }

  for (const key of NUMERIC_FILTER_KEYS) {
    const value = filters[key] as number | undefined
    if (typeof value === 'number') query[key] = String(value)
  }

  if (typeof filters.keyword === 'string' && filters.keyword !== '') query.keyword = filters.keyword
  if (typeof filters.isTrend === 'boolean') query.isTrend = filters.isTrend ? 'true' : 'false'
  if (filters.winnerCountUndetectable) query.winnerCountUndetectable = 'true'

  return query
}

/**
 * 復元 → 再シリアライズの往復で不正クエリを検出し、補正済みクエリと差分有無（呼び出し側が URL を正規形へ直す）を返す。
 */
export function validateAndCorrectQuery(query: RouteQuery): {
  correctedQuery: Record<string, string>
  hasErrors: boolean
} {
  const state = queryToListState(query)
  const correctedQuery = listStateToQuery(state)

  const managedKeys = new Set<string>([
    'page',
    'limit',
    'sortBy',
    'order',
    'startDatetime',
    'endDatetime',
    'keyword',
    'isTrend',
    'winnerCountUndetectable',
    ...ARRAY_FILTER_KEYS.map(String),
    ...NUMERIC_FILTER_KEYS.map(String)
  ])

  const isArrayKey = (key: string): boolean => (ARRAY_FILTER_KEYS as string[]).includes(key)
  const normalizeForCompare = (key: string, value: string | undefined): string | undefined => {
    if (value === undefined) return undefined
    return isArrayKey(key)
      ? value
          .split(',')
          .map(v => v.trim())
          .filter(Boolean)
          .sort()
          .join(',')
      : value
  }

  let hasErrors = false
  const seen = new Set<string>()

  for (const key of Object.keys(query)) {
    if (!managedKeys.has(key)) continue
    seen.add(key)
    const original = normalizeForCompare(key, normalizeRouteValue(query[key]))
    const corrected = normalizeForCompare(key, key in correctedQuery ? correctedQuery[key] : undefined)
    if (original !== corrected) {
      hasErrors = true
      break
    }
  }

  if (!hasErrors) {
    for (const key of Object.keys(correctedQuery)) {
      if (managedKeys.has(key) && !seen.has(key)) {
        hasErrors = true
        break
      }
    }
  }

  return { correctedQuery, hasErrors }
}

/**
 * ルートクエリ値を比較用の文字列へ正規化する（配列はソートして結合）。
 */
function normalizeRouteValue(value: RouteQueryValue): string | undefined {
  if (value === undefined || value === null) return undefined
  if (Array.isArray(value)) {
    return [...value.filter((v): v is string => typeof v === 'string')].sort().join(',')
  }
  return value
}
